import { AppDate } from '../envTypes';

import { NotificationPayloadType } from '../NotificationPayloadType';
import { ModelBase } from './base';
import { Profile } from './profile';

export interface SellerAdminNotification {
  createdAt: AppDate;
  read: boolean;
  readAt?: AppDate;
  type: 'download' | 'feedback' | 'sendItem';
  title?: string;
  content: string;
  from: string;
  to: string;
}

export enum CustomNotificationType {
  UserGroupUsers = 'UserGroupUsers',
  ProfileFollowers = 'ProfileFollowers',
  ProfileFollowing = 'ProfileFollowing',
  Profile = 'Profile',
}

export enum CustomNotificationStatus {
  Processing = 'Processing',
  Success = 'Success',
  Failed = 'Failed',
}

export interface CustomNotification extends ModelBase {
  type: CustomNotificationType;
  sourceIds: string[];
  payload: NotificationPayloadType;
  title: string;
  status: CustomNotificationStatus;
  body?: string;
  error?: string;
  failureCount?: number;
  successCount?: number;
  senderAccount: string;
  senderId: string;
  imageUrl?: string;

  // ─── per-campaign analytics ────────────────────────────────────────────────
  // Denormalised counters, written alongside the `recipients` subcollection so
  // the Record list can show a campaign's outcome without reading one document
  // per device.

  /** Device tokens this campaign was actually sent to. */
  recipientCount?: number;
  /**
   * Distinct users the campaign resolved to, tokens or not. Together with
   * `usersWithoutTokenCount` this separates "we could not reach them" from "we
   * tried and FCM rejected it" — the two are indistinguishable in
   * `failureCount` alone, and conflating them makes an audience with a low
   * notification opt-in rate look like a delivery bug.
   */
  targetedUserCount?: number;
  /** Of `targetedUserCount`, how many had no usable push token. */
  usersWithoutTokenCount?: number;
  /** Recipients that were tapped at least once (unique, not total taps). */
  clickCount?: number;
  /**
   * Taps that arrived with no matching recipient record — a campaign sent
   * before this feature existed, or a device that re-registered its session
   * between the send and the tap. Counted separately so it can never inflate
   * `clickCount` past `recipientCount`.
   */
  orphanClickCount?: number;
}

/** Outcome of handing one device token to FCM. */
export enum PushRecipientStatus {
  /** FCM accepted the message for this token. NOT a guarantee it arrived. */
  Accepted = 'Accepted',
  /** FCM rejected it — see `errorCode`. */
  Failed = 'Failed',
}

/**
 * customNotifications/{notificationId}/recipients/{userId}_{sessionId}
 *
 * One document per device token a campaign was sent to. The id is derived from
 * the session rather than auto-generated so a retried sub-job overwrites its
 * own rows instead of doubling them, and so the click beacon can address a row
 * directly from what the WebView already knows (`userId` + `sessionId`).
 *
 * The push token itself is deliberately NOT stored — only `tokenTail`, which is
 * enough to correlate a row with an FCM error in a log and useless to anyone
 * who reads the collection.
 */
export interface PushNotificationRecipient {
  notificationId: string;
  userId: string;
  sessionId: string;
  /** Last 8 characters of the FCM token, for debugging only. */
  tokenTail: string;
  platform?: string;
  deviceModel?: string;
  status: PushRecipientStatus;
  /** FCM's message id on success — the join key for a later BigQuery export. */
  messageId?: string;
  /** e.g. `messaging/registration-token-not-registered`. */
  errorCode?: string;
  errorMessage?: string;
  sentAt: AppDate;
  /** First tap. Absent means never opened. */
  clickedAt?: AppDate;
  /** Total taps, including repeats after the first. */
  clickCount?: number;
}

export type WatchedSeller = Profile & { watchedAt: AppDate };

export interface RandomPickWinner extends ModelBase {
  profile: Profile;
  profileId: string;
}
